import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

// Same cut-offs the API uses for its signals (clinical-signals.util.ts), so a tinted value here
// always matches the signal rows above.
const RED = '#C62828';
const AMBER = '#B45309';
const ABSENT_OR_REVERSED_FLOW =
  /\b(absent|revers\w*|a?\/?redf|aedf|no\s+edf|no\s+(?:end[\s-]*)?diastolic|zero\s+(?:end[\s-]*)?diastolic)\b/i;

const ordinalPercentile = (percentile) => {
  const shown = percentile < 10 ? String(Math.round(percentile * 10) / 10) : String(Math.round(percentile));
  const lastTwo = Number(shown.slice(-2));
  const last = Number(shown.slice(-1));
  if (!shown.includes('.') && lastTwo >= 11 && lastTwo <= 13) return `${shown}th`;
  return `${shown}${last === 1 ? 'st' : last === 2 ? 'nd' : last === 3 ? 'rd' : 'th'}`;
};

const percentileColor = (p) => (p == null ? undefined : p < 3 ? RED : p < 10 || p > 90 ? AMBER : undefined);
const fluidColor = (row) => {
  if (row.afiCm != null) return row.afiCm < 5 || row.afiCm > 25 ? RED : undefined;
  if (row.sdpCm != null) return row.sdpCm < 2 || row.sdpCm > 8 ? RED : undefined;
  return undefined;
};
const cprOf = (row) =>
  row.cpr != null ? row.cpr : row.mcaPi != null && row.uaPi ? Math.round((row.mcaPi / row.uaPi) * 100) / 100 : null;

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' });
};

const formatGa = (row) => {
  if (row.gestationalAgeLabel) return row.gestationalAgeLabel;
  if (row.gestationalAgeWeeks == null) return '—';
  const whole = Math.floor(row.gestationalAgeWeeks);
  const days = Math.round((row.gestationalAgeWeeks - whole) * 7);
  return days ? `${whole}w ${days}d` : `${whole}w`;
};

// The values a scan row can carry; a row with none of them is not shown.
const MEASUREMENT_FIELDS = [
  'gestationalAgeWeeks', 'efwGrams', 'bpdMm', 'hcMm', 'acMm', 'flMm', 'afiCm', 'sdpCm',
  'uaPi', 'uaFlowStatus', 'mcaPi', 'cpr', 'placentalGrade', 'growthStatus',
];

const dash = (value, unit = '') => (value == null || value === '' ? '—' : `${value}${unit}`);

// Tight padding (8px a side instead of MUI's 16px) so all ten columns fit the chart's width.
const headCell = { fontSize: '11px', fontWeight: 600, color: '#6B7280', py: 1, px: 1, whiteSpace: 'nowrap' };
const bodyCell = { fontSize: '12px', color: '#1f2937', py: 1, px: 1, whiteSpace: 'nowrap' };

/** Every growth scan's measurements side by side, newest first, with abnormal values tinted. */
const GrowthMeasurementsTable = ({ rows }) => {
  if (!Array.isArray(rows)) return null;
  // A scan with nothing measured (e.g. a Growth Scan opened but left empty) is just noise here.
  const measured = rows.filter((row) => MEASUREMENT_FIELDS.some((field) => row[field] != null && row[field] !== ''));
  if (measured.length === 0) return null;
  // The API lists scans oldest first (undated ones before them); newest first reads better here.
  const scans = [...measured].reverse();

  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ fontSize: '11px', color: '#6B7280', letterSpacing: 0.5, textTransform: 'uppercase', mb: 1 }}>
        Scan measurements
      </Typography>
      <TableContainer sx={{ border: '1px solid #eaeaea', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F9FAFB' }}>
              <TableCell sx={headCell}>Date</TableCell>
              <TableCell sx={headCell}>GA</TableCell>
              <TableCell sx={headCell}>EFW</TableCell>
              <TableCell sx={headCell}>%ile</TableCell>
              <TableCell sx={headCell}>Fluid</TableCell>
              <TableCell sx={headCell}>UA PI</TableCell>
              <TableCell sx={headCell}>Flow</TableCell>
              <TableCell sx={headCell}>MCA PI</TableCell>
              <TableCell sx={headCell}>CPR</TableCell>
              <TableCell sx={headCell}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {scans.map((row, index) => {
              const cpr = cprOf(row);
              const flowAbnormal = ABSENT_OR_REVERSED_FLOW.test(row.uaFlowStatus ?? '');
              const fluid =
                row.afiCm != null ? `AFI ${row.afiCm}` : row.sdpCm != null ? `SDP ${row.sdpCm}` : '—';
              return (
                <TableRow key={row.antenatalTestId ?? index} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell sx={bodyCell}>{formatDate(row.scannedAt)}</TableCell>
                  <TableCell sx={bodyCell}>{formatGa(row)}</TableCell>
                  <TableCell sx={bodyCell}>{row.efwGrams != null ? `${Math.round(row.efwGrams)} g` : '—'}</TableCell>
                  <TableCell sx={{ ...bodyCell, color: percentileColor(row.efwPercentile) ?? bodyCell.color, fontWeight: 600 }}>
                    {row.efwPercentile != null ? (
                      ordinalPercentile(row.efwPercentile)
                    ) : (
                      <Tooltip title={row.percentileNote ?? 'Not available'} arrow>
                        <span style={{ color: '#9CA3AF', cursor: 'help' }}>n/a</span>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ ...bodyCell, color: fluidColor(row) ?? bodyCell.color }}>{fluid}</TableCell>
                  <TableCell sx={{ ...bodyCell, color: row.uaPi > 1.5 ? AMBER : bodyCell.color }}>{dash(row.uaPi)}</TableCell>
                  <TableCell sx={{ ...bodyCell, color: flowAbnormal ? RED : bodyCell.color, fontWeight: flowAbnormal ? 600 : 400 }}>
                    {dash(row.uaFlowStatus)}
                  </TableCell>
                  <TableCell sx={bodyCell}>{dash(row.mcaPi)}</TableCell>
                  <TableCell sx={{ ...bodyCell, color: cpr != null && cpr < 1 ? AMBER : bodyCell.color }}>{dash(cpr)}</TableCell>
                  <TableCell sx={{ ...bodyCell, color: ['SGA', 'FGR'].includes(row.growthStatus) ? AMBER : bodyCell.color }}>
                    {dash(row.growthStatus)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default GrowthMeasurementsTable;
